import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Medal, Award, Target, Zap, Users, Trophy, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AIGenerateButton, AIGenerateModal, AIGenerateContext } from '@/components/ai';
import { useAIGenerate } from '@/hooks/useAIGenerate';

interface BadgeConfigPanelProps {
  companyId: string;
}

const BADGE_ICONS = ['🏆', '🎖️', '⭐', '🌟', '💎', '🔥', '⚡', '🎯', '🏅', '👑', '🚀', '💪'];

const CRITERIA_TYPES = [
  { value: 'simulations_completed', label: 'Simulations Completed', icon: Target },
  { value: 'goals_reached', label: 'Goals Reached', icon: Trophy },
  { value: 'score_threshold', label: 'Score Threshold', icon: Award },
  { value: 'streak_days', label: 'Streak Days', icon: Zap },
  { value: 'cpd_points', label: 'CPD Points Earned', icon: Medal },
  { value: 'perfect_scores', label: 'Perfect Scores', icon: Users },
];

interface BadgeFormData {
  name: string;
  description: string;
  icon: string;
  criteria_type: string;
  criteria_value: number;
  criteria_description: string;
  reward_cpd_points: number;
  reward_description: string;
  is_active: boolean;
}

const defaultBadge: BadgeFormData = {
  name: '',
  description: '',
  icon: '🏆',
  criteria_type: 'simulations_completed',
  criteria_value: 5,
  criteria_description: '',
  reward_cpd_points: 10,
  reward_description: '',
  is_active: true,
};

export function BadgeConfigPanel({ companyId }: BadgeConfigPanelProps) {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBadge, setEditingBadge] = useState<any>(null);
  const [formData, setFormData] = useState<BadgeFormData>(defaultBadge);

  // AI Generation state
  const { generate: aiGenerate, isLoading: aiLoading } = useAIGenerate();
  const [aiModalOpen, setAiModalOpen] = useState<'badge_name' | 'badge_description' | null>(null);

  const getAIContext = (): AIGenerateContext[] => {
    const context: AIGenerateContext[] = [];
    context.push({ key: 'criteriaType', label: 'Achievement', value: formData.criteria_type.replace(/_/g, ' ') });
    context.push({ key: 'criteriaValue', label: 'Target', value: String(formData.criteria_value) });
    if (formData.name && aiModalOpen === 'badge_description') {
      context.push({ key: 'badgeName', label: 'Badge', value: formData.name });
    }
    return context;
  };

  const handleAIGenerate = async (customPrompt?: string): Promise<string[]> => {
    if (!aiModalOpen) return [];
    return aiGenerate({
      field: aiModalOpen,
      context: {
        criteriaType: formData.criteria_type,
        criteriaValue: String(formData.criteria_value),
        badgeName: formData.name,
        companyContext: 'corporate training',
      },
      customPrompt,
      variations: 3,
      category: 'gamification',
    });
  };

  const applyAIValue = (value: string) => {
    if (aiModalOpen === 'badge_name') {
      setFormData({ ...formData, name: value });
    } else if (aiModalOpen === 'badge_description') {
      setFormData({ ...formData, description: value });
    }
    setAiModalOpen(null);
  };

  // Fetch badges
  const { data: badges = [], isLoading } = useQuery({
    queryKey: ['company-badges', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_badges')
        .select('*')
        .eq('company_id', companyId)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
  });

  // Create badge
  const createMutation = useMutation({
    mutationFn: async (data: BadgeFormData) => {
      const { error } = await supabase
        .from('company_badges')
        .insert({
          company_id: companyId,
          ...data,
          display_order: badges.length,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-badges', companyId] });
      toast.success('Badge created successfully');
      resetForm();
    },
    onError: (error: any) => {
      toast.error('Failed to create badge: ' + error.message);
    },
  });

  // Update badge
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: BadgeFormData }) => {
      const { error } = await supabase
        .from('company_badges')
        .update(data)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-badges', companyId] });
      toast.success('Badge updated successfully');
      resetForm();
    },
    onError: (error: any) => {
      toast.error('Failed to update badge: ' + error.message);
    },
  });

  // Delete badge
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('company_badges')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-badges', companyId] });
      toast.success('Badge deleted');
    },
    onError: (error: any) => {
      toast.error('Failed to delete badge: ' + error.message);
    },
  });

  const resetForm = () => {
    setFormData(defaultBadge);
    setEditingBadge(null);
    setIsDialogOpen(false);
  };

  const handleEdit = (badge: any) => {
    setEditingBadge(badge);
    setFormData({
      name: badge.name,
      description: badge.description,
      icon: badge.icon,
      criteria_type: badge.criteria_type,
      criteria_value: badge.criteria_value,
      criteria_description: badge.criteria_description || '',
      reward_cpd_points: badge.reward_cpd_points || 0,
      reward_description: badge.reward_description || '',
      is_active: badge.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingBadge) {
      updateMutation.mutate({ id: editingBadge.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getCriteriaLabel = (type: string) => {
    return CRITERIA_TYPES.find(c => c.value === type)?.label || type;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Badge Configuration</h2>
          <p className="text-muted-foreground">Create and manage achievement badges for your team</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Badge
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingBadge ? 'Edit Badge' : 'Create New Badge'}</DialogTitle>
              <DialogDescription>
                Define the criteria and rewards for this badge
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-[auto_1fr] gap-4">
                <div>
                  <Label>Icon</Label>
                  <Select
                    value={formData.icon}
                    onValueChange={(v) => setFormData({ ...formData, icon: v })}
                  >
                    <SelectTrigger className="w-20 text-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="grid grid-cols-4 gap-2 p-2">
                        {BADGE_ICONS.map(icon => (
                          <SelectItem key={icon} value={icon} className="text-2xl cursor-pointer">
                            {icon}
                          </SelectItem>
                        ))}
                      </div>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Badge Name</Label>
                    <AIGenerateButton onClick={() => setAiModalOpen('badge_name')} disabled={aiLoading} />
                  </div>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Simulation Master"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Description</Label>
                  <AIGenerateButton onClick={() => setAiModalOpen('badge_description')} disabled={aiLoading} />
                </div>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What this badge represents..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Criteria Type</Label>
                  <Select 
                    value={formData.criteria_type} 
                    onValueChange={(v) => setFormData({ ...formData, criteria_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CRITERIA_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target Value</Label>
                  <Input
                    type="number"
                    value={formData.criteria_value}
                    onChange={(e) => setFormData({ ...formData, criteria_value: parseInt(e.target.value) || 0 })}
                    min={1}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Criteria Description (optional)</Label>
                <Input
                  value={formData.criteria_description}
                  onChange={(e) => setFormData({ ...formData, criteria_description: e.target.value })}
                  placeholder="e.g., Complete 10 simulations with 80%+ score"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>CPD Points Reward</Label>
                  <Input
                    type="number"
                    value={formData.reward_cpd_points}
                    onChange={(e) => setFormData({ ...formData, reward_cpd_points: parseInt(e.target.value) || 0 })}
                    min={0}
                  />
                </div>
                <div>
                  <Label>Additional Reward</Label>
                  <Input
                    value={formData.reward_description}
                    onChange={(e) => setFormData({ ...formData, reward_description: e.target.value })}
                    placeholder="e.g., Certificate"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Active</Label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingBadge ? 'Update Badge' : 'Create Badge'}
                </Button>
              </div>
            </form>

            {/* AI Generation Modal */}
            {aiModalOpen && (
              <AIGenerateModal
                open={!!aiModalOpen}
                onOpenChange={(open) => !open && setAiModalOpen(null)}
                title={aiModalOpen === 'badge_name' ? 'Badge Name' : 'Badge Description'}
                fieldName={aiModalOpen}
                context={getAIContext()}
                onGenerate={handleAIGenerate}
                onApply={applyAIValue}
                isLoading={aiLoading}
                multiline={aiModalOpen === 'badge_description'}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Badge Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="h-48" />
            </Card>
          ))}
        </div>
      ) : badges.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Medal className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No badges created yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create your first badge to motivate your team</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {badges.map((badge: any) => (
            <Card key={badge.id} className={!badge.is_active ? 'opacity-60' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{badge.icon}</span>
                    <div>
                      <CardTitle className="text-lg">{badge.name}</CardTitle>
                      <Badge variant={badge.is_active ? 'default' : 'secondary'} className="mt-1">
                        {badge.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(badge)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => deleteMutation.mutate(badge.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{badge.description}</p>
                <div className="flex items-center gap-2 text-sm">
                  <Target className="h-4 w-4 text-primary" />
                  <span>{getCriteriaLabel(badge.criteria_type)}: {badge.criteria_value}</span>
                </div>
                {badge.reward_cpd_points > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Trophy className="h-4 w-4 text-warning" />
                    <span>+{badge.reward_cpd_points} CPD Points</span>
                  </div>
                )}
                {badge.reward_description && (
                  <div className="flex items-center gap-2 text-sm">
                    <Award className="h-4 w-4 text-success" />
                    <span>{badge.reward_description}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
